import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI } from '../../utils/fetchData';

const MetricCard = ({ title, value, subtitle, color = '#1DB954' }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
        <div className="flex items-center gap-2 mb-2">
            <div 
                className="w-[8px] h-[8px] rounded-full"
                style={{ backgroundColor: color }}
            />
            <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                {title}
            </span>
        </div>
        <div className="text-white text-[18px] font-bold mb-1">{value}</div>
        {subtitle && (
            <div className="text-[rgba(255,255,255,0.5)] text-[10px]">{subtitle}</div>
        )}
    </div>
);

const ContentCard = ({ title, children }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
        <div className="flex items-center gap-2 mb-4">
            <div className="w-[8px] h-[8px] rounded-full bg-[#1DB954]" />
            <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                {title}
            </span>
        </div>
        {children}
    </div>
);

const SpotifyRevenueAnalytics = () => {
    const navigate = useNavigate();
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('week');
    const token = localStorage.getItem('dizeeToken');

    useEffect(() => {
        loadAnalytics();
    }, [filter]);

    const loadAnalytics = async () => {
        try {
            setLoading(true);
            const response = await getDataAPI(`user/analytics/spotify-revenue?filter=${filter}`, token);
            setAnalyticsData(response.data.data.response);
        } catch (error) {
            console.error('Failed to load Spotify revenue analytics:', error);
            setAnalyticsData(null);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount || isNaN(amount)) return '$0.00';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const formatNumber = (num) => {
        if (!num || isNaN(num)) return '0';
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return new Intl.NumberFormat('en-US').format(num);
    };

    const formatPercentage = (num) => {
        if (!num || isNaN(num)) return '0%';
        return `${num.toFixed(1)}%`;
    };

    if (loading) {
        return (
            <LayoutHeader>
                <div className="w-[390px] h-screen bg-black text-white flex items-center justify-center">
                    <p className="text-[12px]">Loading Spotify revenue analytics...</p>
                </div>
            </LayoutHeader>
        );
    }

    return (
        <LayoutHeader>
            <div className="w-[390px] h-screen bg-black text-white relative">
                <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between">
                        <div className="flex items-center gap-2">
                            <div 
                                className="w-[24px] h-[24px] rounded-md flex items-center justify-center text-[8px] font-medium text-white"
                                style={{ backgroundColor: '#1DB954' }}
                            >
                                SR
                            </div>
                            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Spotify Revenue Analytics</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <p className='text-[red] cursor-pointer text-[12px]'>Export data</p>
                            <div 
                                className="text-white cursor-pointer text-[12px] hover:text-gray-400 transition-colors"
                                onClick={() => navigate('/analytics/hub')}
                            >
                                Back
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="w-full px-4 flex-1 overflow-y-auto pb-[100px]">
                        {/* Filter */}
                        <div className="mb-6">
                            <select 
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                className="bg-[#1A1A1A] border border-[#333] text-white text-[12px] px-3 py-2 rounded"
                            >
                                <option value="day">Last 24 Hours</option>
                                <option value="week">Last Week</option>
                                <option value="month">Last Month</option>
                                <option value="year">Last Year</option>
                                <option value="all">All Time</option>
                            </select>
                        </div>

                        {analyticsData ? (
                            <div className="space-y-4">
                                {/* Top Metrics Row */}
                                <div className="grid grid-cols-2 gap-4">
                                    <MetricCard
                                        title="Total Revenue"
                                        value={formatCurrency(analyticsData.totalRevenue || 0)}
                                        subtitle="Spotify streaming"
                                    />
                                    <MetricCard
                                        title="Total Streams"
                                        value={formatNumber(analyticsData.totalStreams || 0)}
                                        subtitle="All tracks"
                                    />
                                </div>

                                {/* Second Metrics Row */}
                                <div className="grid grid-cols-2 gap-4">
                                    <MetricCard
                                        title="Avg Per Stream"
                                        value={formatCurrency(analyticsData.avgPerStream || 0)}
                                        subtitle="Revenue per stream"
                                    />
                                    <MetricCard
                                        title="Growth Rate"
                                        value={formatPercentage(analyticsData.growthRate || 0)}
                                        subtitle="vs previous period"
                                    />
                                </div>

                                {/* Top Earning Tracks */}
                                <ContentCard title="Top Earning Tracks">
                                    <div className="space-y-3">
                                        {analyticsData.topEarningTracks && analyticsData.topEarningTracks.length > 0 ? (
                                            analyticsData.topEarningTracks.map((track, index) => (
                                                <div key={index} className="flex items-center justify-between py-2 border-b border-[rgba(255,255,255,0.1)] last:border-b-0">
                                                    <div className="flex-1">
                                                        <div className="text-white text-[12px] font-medium">{track.title}</div>
                                                        <div className="text-[rgba(255,255,255,0.7)] text-[10px]">
                                                            {formatNumber(track.streams)} streams
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-[#1DB954] text-[12px] font-bold">{formatCurrency(track.revenue)}</div>
                                                        <div className="text-[rgba(255,255,255,0.5)] text-[10px]">earned</div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-[rgba(255,255,255,0.5)] text-[12px]">No revenue data available</p>
                                        )}
                                    </div>
                                </ContentCard>

                                {/* Revenue Breakdown */}
                                <ContentCard title="Revenue Breakdown">
                                    <div className="space-y-3">
                                        {analyticsData.revenueBreakdown ? (
                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between py-1">
                                                    <span className="text-white text-[12px]">Premium Streams</span>
                                                    <div className="text-right">
                                                        <div className="text-[#1DB954] text-[12px] font-bold">
                                                            {formatCurrency(analyticsData.revenueBreakdown.premium || 0)}
                                                        </div>
                                                        <div className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                                            {formatNumber(analyticsData.revenueBreakdown.premiumStreams || 0)} streams
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between py-1">
                                                    <span className="text-white text-[12px]">Ad-Supported</span>
                                                    <div className="text-right">
                                                        <div className="text-[#1DB954] text-[12px] font-bold">
                                                            {formatCurrency(analyticsData.revenueBreakdown.adSupported || 0)}
                                                        </div>
                                                        <div className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                                            {formatNumber(analyticsData.revenueBreakdown.adSupportedStreams || 0)} streams
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-[rgba(255,255,255,0.5)] text-[12px]">No breakdown data available</p>
                                        )}
                                    </div>
                                </ContentCard>

                                {/* Geographic Revenue */}
                                <ContentCard title="Top Markets">
                                    <div className="space-y-3">
                                        {analyticsData.topMarkets && analyticsData.topMarkets.length > 0 ? (
                                            analyticsData.topMarkets.map((market, index) => (
                                                <div key={index} className="flex items-center justify-between py-2 border-b border-[rgba(255,255,255,0.1)] last:border-b-0">
                                                    <div className="flex-1">
                                                        <div className="text-white text-[12px] font-medium">{market.country}</div>
                                                        <div className="text-[rgba(255,255,255,0.7)] text-[10px]">
                                                            {formatNumber(market.streams)} streams
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-[#1DB954] text-[12px] font-bold">{formatCurrency(market.revenue)}</div>
                                                        <div className="text-[rgba(255,255,255,0.5)] text-[10px]">{formatPercentage(market.percentage)} of total</div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-[rgba(255,255,255,0.5)] text-[12px]">No market data available</p>
                                        )}
                                    </div>
                                </ContentCard>

                                {/* Recent Payments */}
                                <ContentCard title="Recent Payments">
                                    <div className="space-y-3">
                                        {analyticsData.recentPayments && analyticsData.recentPayments.length > 0 ? (
                                            analyticsData.recentPayments.map((payment, index) => (
                                                <div key={index} className="flex items-center justify-between py-2 border-b border-[rgba(255,255,255,0.1)] last:border-b-0">
                                                    <div className="flex-1">
                                                        <div className="text-white text-[12px] font-medium">{payment.period}</div>
                                                        <div className="text-[rgba(255,255,255,0.7)] text-[10px]">
                                                            Paid on {payment.paymentDate}
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-[#1DB954] text-[12px] font-bold">{formatCurrency(payment.amount)}</div>
                                                        <div className="text-[rgba(255,255,255,0.5)] text-[10px]">{payment.status}</div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-[rgba(255,255,255,0.5)] text-[12px]">No payment history available</p>
                                        )}
                                    </div>
                                </ContentCard>
                            </div>
                        ) : (
                            <div className="text-center py-10">
                                <p className="text-[rgba(255,255,255,0.5)] text-[12px]">No Spotify revenue data available</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </LayoutHeader>
    );
};

export default SpotifyRevenueAnalytics; 